/**
 * Questionnaire de satisfaction après intervention (décision du 12/10/2026).
 *
 * La note globale (1 à 5) et le commentaire libre sont toujours proposés et
 * jamais obligatoires (Q89). Chaque entreprise ajoute ses propres questions ;
 * celles-ci sont figées dans la demande envoyée, pour qu'une réponse reste
 * lisible même après modification du questionnaire.
 */
import { z } from 'zod';

export const FEEDBACK_QUESTION_KINDS = ['rating', 'yes_no', 'text'] as const;
export type FeedbackQuestionKind = (typeof FEEDBACK_QUESTION_KINDS)[number];
export const FEEDBACK_MAX_QUESTIONS = 8;

export const feedbackQuestion = z
  .object({
    key: z.string().regex(/^[a-z0-9_-]{1,40}$/),
    label: z.string().trim().min(3).max(200),
    kind: z.enum(FEEDBACK_QUESTION_KINDS),
  })
  .strict();
export type FeedbackQuestion = z.infer<typeof feedbackQuestion>;

export const feedbackSettingsBody = z
  .object({
    autoSend: z.boolean(),
    questions: z
      .array(feedbackQuestion)
      .max(FEEDBACK_MAX_QUESTIONS)
      .refine((items) => new Set(items.map((q) => q.key)).size === items.length, 'Questions en double.'),
  })
  .strict();

export const feedbackAnswer = z
  .object({
    key: z.string().regex(/^[a-z0-9_-]{1,40}$/),
    value: z.union([z.number().int().min(1).max(5), z.boolean(), z.string().trim().min(1).max(1000)]),
  })
  .strict();

export const feedbackResponseBody = z
  .object({
    rating: z.number().int().min(1).max(5).optional(),
    comment: z.string().trim().max(2000).optional(),
    answers: z.array(feedbackAnswer).max(FEEDBACK_MAX_QUESTIONS).default([]),
  })
  .strict();
export type FeedbackResponseBody = z.infer<typeof feedbackResponseBody>;

/** Réponse enregistrée : la question est recopiée avec la valeur. */
export interface FeedbackStoredAnswer {
  key: string;
  label: string;
  kind: FeedbackQuestionKind;
  value: number | boolean | string;
}

/**
 * Vérifie les réponses contre les questions figées de la demande : une
 * question inconnue, en double ou d'un type différent est refusée.
 */
export function matchFeedbackAnswers(
  questions: readonly FeedbackQuestion[],
  answers: FeedbackResponseBody['answers'],
): FeedbackStoredAnswer[] | string {
  const seen = new Set<string>();
  const stored: FeedbackStoredAnswer[] = [];
  for (const answer of answers) {
    const question = questions.find((q) => q.key === answer.key);
    if (!question || seen.has(answer.key)) return 'Réponse à une question inconnue.';
    seen.add(answer.key);
    const valid =
      question.kind === 'rating'
        ? typeof answer.value === 'number'
        : question.kind === 'yes_no'
          ? typeof answer.value === 'boolean'
          : typeof answer.value === 'string';
    if (!valid) return `Réponse invalide pour « ${question.label} ».`;
    stored.push({ key: question.key, label: question.label, kind: question.kind, value: answer.value });
  }
  return stored;
}
